import { normalizeLocalSnapshot, readLocalDataStrict, withLocalDataLock, writeLocalData } from '../dataPortability';
import { stableLocalValue as stable } from '../localPurchaseIntegrity';
import { sha256 } from '../fileHash.mjs';
import { planLocalBankStatementFinalize } from './bankStatementDraft';

const assert = (condition, message) => { if (!condition) throw new Error(message); };
const withoutRecoveryHashes = (data, itemId) => ({ ...data, intakeItems: data.intakeItems.map(item => {
  if (item.id !== itemId || !item.bankFinalReceipt) return item;
  const { beforeSnapshotHash, afterSnapshotHash, ...receipt } = item.bankFinalReceipt;
  return { ...item, bankFinalReceipt: receipt };
}) });

export async function commitLocalBankStatementFinalize({ freshness, space, getSnapshot, isCurrent, userId, buildActivity = value => value, ...input }) {
  try {
    assert(typeof isCurrent === 'function' && isCurrent(), 'Reopen this statement in its original local workspace.');
    const ui = normalizeLocalSnapshot(getSnapshot()), beforeHash = await sha256(stable(ui));
    return await (freshness ? freshness.withLock : action => withLocalDataLock(space, action))(async () => {
      assert(isCurrent(), 'The active Banking workspace changed.');
      const { raw, data } = readLocalDataStrict(space);
      freshness?.check(raw);
      assert(data.profile?.id === userId && !data.profile.publicMode, 'The local account changed. Reopen Banking.');
      const business = data.businesses.find(item => item.id === input.business.id && item.workspaceId === input.organizationId);
      assert(business, 'This statement business is no longer available.');
      const plan = planLocalBankStatementFinalize({ ...input, data, business, profile: data.profile });
      assert(plan.ok, plan.error);
      const same = stable(ui) === stable(data);
      if (plan.noop) {
        const afterHash = await sha256(stable(withoutRecoveryHashes(data, input.itemId)));
        assert(same || (plan.receipt.beforeSnapshotHash === beforeHash && plan.receipt.afterSnapshotHash === afterHash),
          'The original confirmation is saved, but other local edits changed. Reload before recovering it.');
        assert(isCurrent(), 'The active Banking workspace changed.');
        return { ...plan, data, localCommit: freshness?.recover(data, raw) };
      }
      assert(same, 'Local changes are still saving or another view changed this workspace. Wait briefly, then retry; reload if it persists.');
      const next = { ...plan.data, activityLog: [plan.audit, ...(plan.auditEvents || [])].map(buildActivity).concat(data.activityLog || []) };
      const receipt = { ...plan.receipt, beforeSnapshotHash: beforeHash, afterSnapshotHash: await sha256(stable(withoutRecoveryHashes(next, input.itemId))) };
      next.intakeItems = next.intakeItems.map(item => item.id === input.itemId ? { ...item, bankFinalReceipt: receipt } : item);
      assert(isCurrent(), 'The active Banking workspace changed.');
      const saved = freshness ? freshness.write(next) : writeLocalData(next, space, { expectedRaw: raw });
      assert(saved.ok, 'The complete confirmation could not be saved. Nothing was applied; free browser storage and retry.');
      return { ...plan, data: next, receipt, localCommit: saved.localCommit };
    });
  } catch (error) { return { ok: false, error: error.message }; }
}
